import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Select } from '../components/Select';

export const ReadingHistoryPage = () => {
  const [filterYear, setFilterYear] = useState('all');
  const [filterGenre, setFilterGenre] = useState('all');

  // Mock data - in real app, would fetch from API
  const completedReads = [
    {
      id: 1,
      manuscript: {
        id: 201,
        title: 'The Silent Compass',
        author: 'Jennifer Blake',
        genre: 'Mystery',
        wordCount: 84000,
      },
      completedDate: '2024-01-15',
      startedDate: '2023-12-20',
      readingTime: '26 days',
      feedbackGiven: true,
      feedbackDate: '2024-01-18',
      rating: 4,
      writerResponse: 'Thank you so much for the detailed feedback! Your insights on pacing were especially helpful.',
      writerRating: 5,
    },
    {
      id: 2,
      manuscript: {
        id: 202,
        title: 'Beneath the Autumn Sky',
        author: 'Marcus Reynolds',
        genre: 'Literary Fiction',
        wordCount: 72000,
      },
      completedDate: '2023-12-10',
      startedDate: '2023-11-15',
      readingTime: '25 days',
      feedbackGiven: true,
      feedbackDate: '2023-12-12',
      rating: 5,
      writerResponse: 'Your character analysis was spot-on. I made several revisions based on your suggestions.',
      writerRating: 5,
    },
    {
      id: 3,
      manuscript: {
        id: 203,
        title: 'Starborn Legacy',
        author: 'Aisha Patel',
        genre: 'Science Fiction',
        wordCount: 98000,
      },
      completedDate: '2023-11-05',
      startedDate: '2023-10-10',
      readingTime: '26 days',
      feedbackGiven: true,
      feedbackDate: '2023-11-08',
      rating: 4,
      writerResponse: null,
      writerRating: null,
    },
    {
      id: 4,
      manuscript: {
        id: 204,
        title: 'The Herbalist\'s Daughter',
        author: 'Emma Thompson',
        genre: 'Historical Fiction',
        wordCount: 89000,
      },
      completedDate: '2023-10-20',
      startedDate: '2023-09-25',
      readingTime: '25 days',
      feedbackGiven: true,
      feedbackDate: '2023-10-22',
      rating: 5,
      writerResponse: 'Your historical accuracy notes were invaluable! Thank you for such thorough feedback.',
      writerRating: 5,
    },
    {
      id: 5,
      manuscript: {
        id: 205,
        title: 'Shadow of the Dragon',
        author: 'Wei Chen',
        genre: 'Fantasy',
        wordCount: 105000,
      },
      completedDate: '2023-09-15',
      startedDate: '2023-08-18',
      readingTime: '28 days',
      feedbackGiven: true,
      feedbackDate: '2023-09-18',
      rating: 4,
      writerResponse: 'Thanks for the worldbuilding feedback - exactly what I needed!',
      writerRating: 5,
    },
    {
      id: 6,
      manuscript: {
        id: 206,
        title: 'Midnight Bloom',
        author: 'Sophie Laurent',
        genre: 'Romance',
        wordCount: 68000,
      },
      completedDate: '2023-08-10',
      startedDate: '2023-07-20',
      readingTime: '21 days',
      feedbackGiven: true,
      feedbackDate: '2023-08-12',
      rating: 3,
      writerResponse: 'I appreciate your honest feedback about the pacing issues.',
      writerRating: 4,
    },
  ];

  // Calculate statistics
  const stats = {
    totalReads: completedReads.length,
    averageRating: completedReads.reduce((acc, read) => acc + read.rating, 0) / completedReads.length,
    averageReaderRating:
      completedReads
        .filter(read => read.writerRating)
        .reduce((acc, read) => acc + (read.writerRating || 0), 0) /
      completedReads.filter(read => read.writerRating).length,
    totalWords: completedReads.reduce((acc, read) => acc + read.manuscript.wordCount, 0),
    feedbackResponseRate: Math.round(
      (completedReads.filter(read => read.writerResponse).length / completedReads.length) * 100
    ),
  };

  // Filters
  const years = ['All Years', '2024', '2023', '2022'];
  const genres = ['All Genres', 'Fantasy', 'Science Fiction', 'Mystery', 'Romance', 'Historical Fiction', 'Literary Fiction'];

  const filteredReads = completedReads.filter(read => {
    const year = new Date(read.completedDate).getFullYear().toString();
    const matchesYear = filterYear === 'all' || year === filterYear;
    const matchesGenre = filterGenre === 'all' || read.manuscript.genre === filterGenre;
    return matchesYear && matchesGenre;
  });

  const renderStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Reading History
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Your completed beta reading projects
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '4px',
            }}>
              {stats.totalReads}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Books Read
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.warning,
              marginBottom: '4px',
            }}>
              {stats.averageRating}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Avg Rating Given
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '4px',
            }}>
              {stats.averageReaderRating.toFixed(1)}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Your Rating
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '4px',
            }}>
              {(stats.totalWords / 1000000).toFixed(1)}M
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Words Read
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '4px',
            }}>
              {stats.feedbackResponseRate}%
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Response Rate
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}>
            <Select
              label="Filter by Year"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              options={years.map(year => ({
                value: year === 'All Years' ? 'all' : year,
                label: year,
              }))}
            />

            <Select
              label="Filter by Genre"
              value={filterGenre}
              onChange={(e) => setFilterGenre(e.target.value)}
              options={genres.map(genre => ({
                value: genre === 'All Genres' ? 'all' : genre,
                label: genre,
              }))}
            />
          </div>
        </Card>

        {/* Reading History List */}
        {filteredReads.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                No completed reads found
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setFilterYear('all');
                  setFilterGenre('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {filteredReads.map((read) => (
              <Card key={read.id}>
                <div>
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        marginBottom: '4px',
                      }}>
                        {read.manuscript.title}
                      </h3>
                      <p style={{
                        fontSize: '16px',
                        color: theme.colors.textSecondary,
                        margin: 0,
                      }}>
                        by {read.manuscript.author}
                      </p>
                    </div>
                    <Badge variant="success">Completed</Badge>
                  </div>

                  {/* Metadata */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                  }}>
                    <span>{read.manuscript.genre}</span>
                    <span>•</span>
                    <span>{read.manuscript.wordCount.toLocaleString()} words</span>
                    <span>•</span>
                    <span>Completed: {read.completedDate}</span>
                    <span>•</span>
                    <span>Reading time: {read.readingTime}</span>
                  </div>

                  {/* Rating Given */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: theme.colors.backgroundAlt,
                    borderRadius: theme.borderRadius.small,
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px',
                    }}>
                      <div>
                        <div style={{
                          fontSize: '13px',
                          color: theme.colors.textSecondary,
                          marginBottom: '4px',
                        }}>
                          Your rating:
                        </div>
                        <div style={{ fontSize: '18px' }}>
                          {renderStars(read.rating)}
                        </div>
                      </div>

                      {read.writerRating && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            color: theme.colors.textSecondary,
                            marginBottom: '4px',
                          }}>
                            Author rated you:
                          </div>
                          <div style={{ fontSize: '18px' }}>
                            {renderStars(read.writerRating)}
                          </div>
                        </div>
                      )}

                      {read.feedbackGiven && (
                        <Badge variant="success">
                          Feedback given {read.feedbackDate}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Writer Response */}
                  {read.writerResponse && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: theme.colors.primaryLight,
                      borderRadius: theme.borderRadius.small,
                      marginBottom: '16px',
                      borderLeft: `4px solid ${theme.colors.primary}`,
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: theme.colors.textSecondary,
                        marginBottom: '8px',
                      }}>
                        Author's response:
                      </div>
                      <p style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        margin: 0,
                        fontStyle: 'italic',
                      }}>
                        "{read.writerResponse}"
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    <Button
                      size="small"
                      variant="outline"
                      onClick={() => window.alert(`Viewing your feedback for "${read.manuscript.title}"`)}
                    >
                      View Your Feedback
                    </Button>

                    <Button
                      size="small"
                      variant="outline"
                      onClick={() => window.location.href = `/manuscripts/${read.manuscript.id}/preview`}
                    >
                      View Manuscript
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Insights Card */}
        <Card style={{
          backgroundColor: theme.colors.backgroundAlt,
          marginTop: '40px',
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Your Beta Reading Journey
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
          }}>
            <div>
              <strong style={{ color: theme.colors.textPrimary }}>Most Read Genre:</strong>
              <div style={{ marginTop: '4px' }}>
                {completedReads.reduce((acc, read) => {
                  acc[read.manuscript.genre] = (acc[read.manuscript.genre] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>) &&
                  Object.entries(
                    completedReads.reduce((acc, read) => {
                      acc[read.manuscript.genre] = (acc[read.manuscript.genre] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'
                }
              </div>
            </div>
            <div>
              <strong style={{ color: theme.colors.textPrimary }}>Average Reading Speed:</strong>
              <div style={{ marginTop: '4px' }}>
                ~{Math.round(stats.totalWords / completedReads.length / 25).toLocaleString()} words/day
              </div>
            </div>
            <div>
              <strong style={{ color: theme.colors.textPrimary }}>Feedback Quality:</strong>
              <div style={{ marginTop: '4px' }}>
                {stats.averageReaderRating >= 4.5 ? 'Excellent' :
                 stats.averageReaderRating >= 4.0 ? 'Very Good' :
                 stats.averageReaderRating >= 3.5 ? 'Good' : 'Average'}
                {' '}({stats.averageReaderRating.toFixed(1)}/5)
              </div>
            </div>
          </div>
        </Card>
      </main>

      <Footer />
    </div>
  );
};
